import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Home, Building2, MapPin, DollarSign, Eye, Users,
  Edit2, Trash2, MoreVertical, Loader2, Sparkles, Megaphone,
  CheckCircle, Clock, Pause, Search, Filter, TrendingUp,
  BedDouble, Bath, Maximize, Car
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROPERTY_TYPES = {
  casa: { name: 'Casa', icon: Home, color: '#6366f1' },
  departamento: { name: 'Departamento', icon: Building2, color: '#8b5cf6' },
  terreno: { name: 'Terreno', icon: MapPin, color: '#10b981' },
  local_comercial: { name: 'Local Comercial', icon: Building2, color: '#f59e0b' },
  oficina: { name: 'Oficina', icon: Building2, color: '#3b82f6' },
  bodega: { name: 'Bodega', icon: Building2, color: '#6b7280' },
};

const STATUS_CONFIG = {
  draft: { name: 'Borrador', color: '#6b7280', icon: Clock },
  active: { name: 'Publicada', color: '#10b981', icon: CheckCircle },
  paused: { name: 'Pausada', color: '#f59e0b', icon: Pause },
  sold: { name: 'Vendida', color: '#8b5cf6', icon: CheckCircle },
  rented: { name: 'Rentada', color: '#3b82f6', icon: CheckCircle },
};

export default function CRMPropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [propertiesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/properties/my-properties${filter !== 'all' ? `?status=${filter}` : ''}`, { headers }),
        axios.get(`${API_URL}/api/marketing/dashboard/stats`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setProperties(propertiesRes.data.properties || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error loading properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta propiedad?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/crm/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      alert('Error al eliminar la propiedad');
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Sin precio';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0
    }).format(price);
  };

  const filteredProperties = properties.filter(p => 
    !searchQuery || 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Mis Propiedades</h1>
            <p className="text-gray-500 mt-1">CRM Inmobiliario con IA</p>
          </div>
          <Link
            to="/crm/propiedades/nueva"
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nueva Propiedad
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.properties?.total || 0}</p>
                <p className="text-sm text-gray-500">Propiedades</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.properties?.active || 0}</p>
                <p className="text-sm text-gray-500">Publicadas</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.performance?.total_views || 0}</p>
                <p className="text-sm text-gray-500">Vistas</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.performance?.total_leads || 0}</p>
                <p className="text-sm text-gray-500">Leads</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por título o ciudad..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {[
                { id: 'all', name: 'Todas' },
                { id: 'active', name: 'Publicadas' },
                { id: 'draft', name: 'Borradores' },
                { id: 'paused', name: 'Pausadas' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    filter === f.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Properties List */}
        {filteredProperties.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Home className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {filter === 'all' ? 'No tienes propiedades aún' : `No hay propiedades ${STATUS_CONFIG[filter]?.name.toLowerCase()}`}
            </h3>
            <p className="text-gray-500 mb-6">
              Comienza a publicar tus propiedades y genera más oportunidades
            </p>
            <Link
              to="/crm/propiedades/nueva"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Plus className="w-5 h-5" />
              Agregar Propiedad
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProperties.map(property => {
              const typeConfig = PROPERTY_TYPES[property.property_type] || PROPERTY_TYPES.casa;
              const statusConfig = STATUS_CONFIG[property.status] || STATUS_CONFIG.draft;
              const TypeIcon = typeConfig.icon;
              const StatusIcon = statusConfig.icon;
              const coverImage = property.images?.[0]?.url || property.images?.[0];
              
              return (
                <div
                  key={property.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Image */}
                  <div className="relative h-48 bg-gray-100">
                    {coverImage ? (
                      <img
                        src={coverImage}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <TypeIcon className="w-16 h-16 text-gray-300" />
                      </div>
                    )}
                    
                    {/* Status badge */}
                    <div 
                      className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium text-white flex items-center gap-1"
                      style={{ backgroundColor: statusConfig.color }}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.name}
                    </div>
                    
                    {/* Campaign badge */}
                    {property.has_active_campaign && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 bg-amber-500 rounded-full text-xs font-medium text-white flex items-center gap-1">
                        <Megaphone className="w-3 h-3" />
                        Campaña activa
                      </div>
                    )}
                    
                    {/* Price */}
                    <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-white/95 rounded-lg shadow">
                      <p className="text-lg font-bold text-gray-900">{formatPrice(property.price)}</p>
                      <p className="text-xs text-gray-500 capitalize">{property.operation_type}</p>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{property.title}</h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-3">
                      <MapPin className="w-4 h-4" />
                      {property.colony ? `${property.colony}, ` : ''}{property.city || 'Sin ubicación'}
                    </p>
                    
                    {/* Features */}
                    <div className="flex gap-4 text-sm text-gray-600 mb-4">
                      {property.bedrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <BedDouble className="w-4 h-4" />
                          {property.bedrooms}
                        </span>
                      )}
                      {property.bathrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <Bath className="w-4 h-4" />
                          {property.bathrooms}
                        </span>
                      )}
                      {property.construction_area && (
                        <span className="flex items-center gap-1">
                          <Maximize className="w-4 h-4" />
                          {property.construction_area} m²
                        </span>
                      )}
                    </div>
                    
                    {/* Stats */}
                    <div className="flex gap-4 text-sm text-gray-500 border-t pt-3 mb-3">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {property.views_count || 0} vistas
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {property.leads_count || 0} leads
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <Link
                        to={`/crm/propiedades/${property.id}`}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                      >
                        <Edit2 className="w-4 h-4" />
                        Editar
                      </Link>
                      <Link
                        to={`/crm/propiedades/${property.id}/campana`}
                        className="flex items-center justify-center gap-1 px-3 py-2 bg-amber-100 text-amber-700 text-sm rounded-lg hover:bg-amber-200"
                      >
                        <Megaphone className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(property.id)}
                        className="flex items-center justify-center px-3 py-2 bg-red-100 text-red-600 text-sm rounded-lg hover:bg-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA Banner */}
        <div className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl font-bold">CRM con IA incluido en tu membresía</h3>
                <p className="text-indigo-200">Genera descripciones profesionales y lanza campañas con un clic</p>
              </div>
            </div>
            <Link
              to="/crm/propiedades/nueva"
              className="px-6 py-3 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 whitespace-nowrap"
            >
              Publicar Propiedad
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
